import * as React from 'react';
import {PaletteColor, SimplePaletteColorOptions} from '@mui/material/styles';
import {PaletteMode} from "@mui/material";
import type {} from '@mui/x-data-grid/themeAugmentation';

import '@mui/material/AccordionSummary'
import "@mui/material/styles";
import "@mui/material/Typography";
import "@mui/material/Fab";

/**
 * Import Fonts
 */
import "@fontsource/source-sans-pro";
import "@fontsource/source-sans-pro/300.css";
import "@fontsource/source-sans-pro/300-italic.css";
import "@fontsource/source-sans-pro/400.css";
import "@fontsource/source-sans-pro/400-italic.css";
import "@fontsource/source-sans-pro/600.css";
import "@fontsource/source-sans-pro/600-italic.css";
import "@fontsource/source-sans-pro/700.css";
import "@fontsource/source-sans-pro/700-italic.css";


/**
 * Adding Custom Colors to Palette
 */
declare module "@mui/material/styles" {
  interface Palette {
    middleBlue: PaletteColor;
    lightBlue: PaletteColor;
  }

  interface PaletteOptions {
    middleBlue: SimplePaletteColorOptions;
    lightBlue: SimplePaletteColorOptions;
  }

  interface TypographyVariants {
    highlight: React.CSSProperties;
    label: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    highlight?: React.CSSProperties;
    label?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    highlight: true;
    label: true;
  }
}

// Extending the Variant prop options for Fab (Floating Action Button)
declare module '@mui/material/Fab' {
  interface FabPropsVariantOverrides {
    outlined: true;
    taskSalesActive: true;
    taskSales: true;
    taskPurchaseActive: true;
    taskPurchase: true;
    taskAccountingActive: true;
    taskAccounting: true;
    taskQmActive: true;
    taskQm: true;
    taskPackagingActive: true;
    taskPackaging: true;
    taskLogisticsActive: true;
    taskLogistics: true;
  }
}

// Extending the Variant prop options for Avatar
declare module '@mui/material/Avatar' {
  interface AvatarPropsVariantOverrides {
    primary: true;
    sales: true;
    purchase: true;
    accounting: true;
    qm: true;
    packaging: true;
    logistics: true;
  }
}

// Extending the Variant prop options for Chip
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    sales: true;
    purchase: true;
    accounting: true;
    qm: true;
    packaging: true;
    logistics: true;
  }
}

// Extending the Variant prop options for Table
declare module '@mui/material/Table' {
  interface TablePropsVariantOverrides {
    striped: true;
  }
}

// Adding the own Property options for AccordionSummary
declare module '@mui/material/Table' {
  interface TableOwnProps {
    variant?: string;
  }
}

// Adding the own Property options for AccordionSummary
declare module '@mui/material/AccordionSummary' {
  interface AccordionSummaryOwnProps {
    variant?: string;
  }
}

// Extending the Variant prop options for Tabs
declare module '@mui/material/Tabs' {
  interface TabsOwnProps {
    type?: string;
  }
}

const FactureeTheme = (mode: PaletteMode | undefined) => {
  if (mode !== 'dark')
    mode = 'light';

  const col = {

    /*******************************
     * DARK PALETTE
     *******************************/
    dark: {
      background: {
        default: '#023247', // equivalent to rgba(2, 50, 71, 1),
        main: '#416575', // equivalent to rgba(2, 50, 71, .75),
        paper: '#023247', // equivalent to rgba(2, 50, 71, 1),
        header: 'rgba(22, 146, 178, .15)', //'#dceff3', // equivalent to rgba(22, 146, 178, .15),
        level: {
          l1: 'rgba(22, 146, 178, .18)', //'#d5ebf1', // equivalent to rgba(22, 146, 178, .18),
          l2: 'rgba(22, 146, 178, .13)', //'#e1f1f5', // equivalent to rgba(22, 146, 178, .13),
          l3: 'rgba(22, 146, 178, .08)', //'#ecf6f9', // equivalent to rgba(22, 146, 178, .08),
          l4: 'rgba(22, 146, 178, .04)'  //'#f8fcfd', // equivalent to rgba(22, 146, 178, .03)
        }
      },
      primary: {
        shy: '#f6fbfc', // equivalent to rgba(121, 196, 212, .07),
        light: '#d7edf2', // equivalent to rgba(121, 196, 212, .30),
        medium: '#a8d9e3', // equivalent to rgba(121, 196, 212, .65),
        main: '#79c4d4', // equivalent to rgba(121, 196, 212, 1),
        dark: '#79c4d4', // equivalent to rgba(121, 196, 212, 1), // no darker than main intended
        passive: 'rgba(121, 196, 212, .50)',
        contrast: '#023247', // equivalent to rgba(2, 50, 71, 1)
      },
      tertiary: {
        shy: 'rgba(22, 146, 178, .07)',
        light: 'rgba(22, 146, 178, .30)',
        medium: 'rgba(22, 146, 178, .65)',
        main: '#1692B2',
        dark: '#1692B2', // no darker than main intended
        passive: 'rgba(22, 146, 178, .50)'
      },
      quartary: {
        shy: 'rgba(11, 94, 130, .07)',
        light: 'rgba(11, 94, 130, .30)',
        medium: 'rgba(11, 94, 130, .65)',
        main: '#0b5e82',
        dark: 'rgba(11, 94, 130, 1)', // no darker than main intended
        passive: 'rgba(11, 94, 130, .50)',
        contrast: '#FFF'
      },
      // -- Text
      text: {
        primary: '#79c4d4', // equivalent to rgba(121, 196, 212, 1),
        secondary: '#a8d9e3', // equivalent to rgba(121, 196, 212, .65),
        dark: 'rgba(145, 235, 254, 1)',
        disabled: 'rgba(11, 94, 130, .50)',
        contrast: '#023247', // equivalent to rgba(2, 50, 71, 1)
      },
      disabled: {
        shy: 'rgba(121, 196, 212, .15)', //'#ebf6f9', // equivalent to rgba(121, 196, 212, .15),
        light: 'rgba(121, 196, 212, .30)', //'#d7edf2', // equivalent to rgba(121, 196, 212, .30),
        main: 'rgba(121, 196, 212, .40)'
      },
    },

    /*******************************
     * LIGHT PALETTE
     *******************************/
    light: {
      // -- Background
      background: {
        default: '#fff',
        main: '#fff',
        paper: '#fff',
        header: '#b9dee8', // was: rgba(22, 146, 178, .24), now equiv. rgba(22, 146, 178, .30)
        level: {
          l1: '#dceff3', // equivalent to rgba(22, 146, 178, .18),
          l2: '#e1f1f5', // equivalent to rgba(22, 146, 178, .13),
          l3: '#ecf6f9', // equivalent to rgba(22, 146, 178, .08),
          l4: '#f8fcfd', // equivalent to rgba(22, 146, 178, .03)
        }
      },
      // -- Facturee primary (blue)
      primary: {
        shy: '#eef4f6', // equivalent to rgba(11, 94, 130, .07)
        light: '#b6cfda', // equivalent to rgba(11, 94, 130, .30)
        medium: '#6096ae', // equivalent to rgba(11, 94, 130, .65)
        main: '#0b5e82', // equivalent to rgba(11, 94, 130, 1)
        dark: '#0b5e82', // equivalent to rgba(11, 94, 130, 1) // no darker than main intended
        passive: '#b6cfda', // equivalent to rgba(11, 94, 130, .30)
        contrast: '#fff'
      },
      // -- Facturee secondary (orange)
      secondary: {
        shy: '#fef6f1', // equivalent to rgba(239, 120, 60, .07),
        light: '#fad7c5', // equivalent to rgba(239, 120, 60, .30),
        medium: '#f5a780', // equivalent to rgba(239, 120, 60, .65),
        main: '#ef783c', // equivalent to rgba(239, 120, 60, 1),
        dark: '#ef783c', // equivalent to rgba(239, 120, 60, 1),
        passive: 'rgba(239, 120, 60, .68)'
      },
      // -- Facturee secondary blue
      tertiary: {
        shy: '#f6fbfc', // equivalent to rgba(22, 146, 178, .07),
        light: '#d7edf2', // equivalent to rgba(22, 146, 178, .30),
        medium: '#a8d9e3', // equivalent to rgba(22, 146, 178, .65),
        main: '#1692b2', // equivalent to rgba(22, 146, 178, 1),
        dark: '#1692b2', // no darker than main intended
        passive: ''
      },
      // -- Facturee third blue
      quartary: {
        shy: '#f6fbfc', // equivalent to rgba(121, 196, 212, .07),
        light: '#d7edf2', // equivalent to rgba(121, 196, 212, .30),
        medium: '#a8d9e3', // equivalent to rgba(121, 196, 212, .65),
        main: '#79c4d4', // equivalent to rgba(121, 196, 212, 1),
        dark: '#79c4d4', // no darker than main intended
        passive: ''
      },
      // -- Text
      text: {
        primary: '#0b5e82', // equivalent to rgba(11, 94, 130, 1)
        secondary: '#6096ae', // equivalent to rgba(11, 94, 130, .65)
        dark: '#023247', // equivalent to rgba(2, 50, 71, 1),
        disabled: 'rgba(11, 94, 130, .40)',
        contrast: 'rgba(11, 94, 130, 1)'
      },
      // -- Info
      info: {
        main: '#023247', // equivalent to rgba(2, 50, 71, 1)
      },
      // -- Disabled
      disabled: {
        shy: '#eef4f6', // equivalent to rgba(11, 94, 130, .07)
        light: '#b6cfda', // equivalent to rgba(11, 94, 130, .30)
        main: 'rgba(11, 94, 130, .50)' // was Alpha .40
      },
    },
    /*******************************
     * INFO PALETTE
     *******************************/
    info: {
      main: 'rgba(3, 169, 244, 1)',
      passive: 'rgba(3, 169, 244, .5)'
    },
    // -- Error
    error: {
      main: 'rgba(237, 78, 68, 1)',
      passive: 'rgba(237, 78, 68, .35)',
    },
    // -- Warning
    warning: {
      main: 'rgba(255, 152, 0, 1)',
      passive: 'rgba(255, 152, 0, .35)',
    },
    // -- Success
    success: {
      main: 'rgba(115, 179, 56, 1)',
      passive: 'rgba(115, 179, 56, .35)'
    },

    /*******************************
     * CUSTOM FUNCTIONAL PALETTE
     *******************************/
    sales: {
      main: '#FFC107',
      disabled: 'rgba(255, 193, 7, .35)'
    },
    purchase: {
      main: '#90EE90',
      disabled: 'rgba(144, 238, 144, .35)'
    },
    accounting: {
      main: '#ADD8E6',
      disabled: 'rgba(173, 216, 230, .35)'
    },
    qm: {
      main: '#FFA07A',
      disabled: 'rgba(255, 160, 122, .35)'
    },
    packaging: {
      main: '#B0C4DE',
      disabled: 'rgba(176, 196, 222, .35)'
    },
    logistics: {
      main: '#008B8B',
      disabled: 'rgba(0, 139, 139, .35)'
    },
    fromRequestTool: {
      main: '#3CB371',
      disabled: 'rgba(60, 179, 113, .35)',
    },
    fromEmail: {
      main: '#9B59B6',
      disabled: 'rgba(155, 89, 182, .35)',
    },
    fromDrawing: {
      main: '#A67C52',
      disabled: 'rgba(166, 124, 82, .35)',
    }
  };

  const theme: object /*ThemeOptions*/ = {
    breakpoints: {
      values: {
        xs: 0,
        sm: 480,
        md: 768,
        lg: 1024,
        xl: 1440
      }
    },
    palette: {
      mode,
      // -- Background
      background: {
        ...col[mode].background
      },
      // -- Facturee primary (orange)
      primary: {
        ...col[mode].primary
      },
      // -- Facturee secondary (orange)
      secondary: {
        ...col.light.secondary
      },
      // -- Facturee secondary blue
      tertiary: {
        ...col[mode].tertiary
      },
      quartery: {
        ...col[mode].quartary
      },
      middleBlue: {
        ...col.light.tertiary
      },
      // -- Facturee third blue
      lightBlue: {
        ...col.light.quartary
      },
      // -- Facturee Text
      text: {
        ...col[mode].text
      },
      typography: {
        ...col[mode].text
      },
      sales: {
        ...col.sales,
      },
      purchase: {
        ...col.purchase
      },
      accounting: {
        ...col.accounting
      },
      qm: {
        ...col.qm
      },
      packing: {
        ...col.packaging
      },
      logistics: {
        ...col.logistics
      },
      fromRequestTool: {
        ...col.fromRequestTool
      },
      fromEmail: {
        ...col.fromEmail
      },
      fromDrawing: {
        ...col.fromDrawing
      },
      // -- Info Colors
      ...col.info,
      ...col.error,
      ...col.warning,
      ...col.success,
      // -- Custom Functional Colors (Working Units)
      ...col.sales,
      ...col.purchase,
      ...col.accounting,
      ...col.qm,
      ...col.packaging,
      ...col.logistics,
      // Used by `getContrastText()` to maximize the contrast between
      // the background and the text.
      contrastThreshold: 3,
      // Used by the functions below to shift a color's luminance by approximately
      // two indexes within its tonal palette.
      // E.g., shift from Red 500 to Red 300 or Red 700.
      tonalOffset: 0.2
    },
    typography: {
      fontFamily: `"Source Sans Pro", Helvetica, Arial, sans-serif`,
      htmlFontSize: 16,
      fontSize: 16,
      h1: {
        fontSize: 32,
        fontWeight: 700
      },
      h2: {
        fontSize: 26,
        fontWeight: 600
      },
      h3: {
        fontSize: 22,
        fontWeight: 600
      },
      h4: {
        fontSize: 20,
        fontWeight: 600
      },
      h5: {
        fontSize: 18,
        fontWeight: 600
      },
      h6: {
        fontSize: 17.25,
        fontWeight: 600,
        color: col[mode].text.secondary,
      },
      subtitle1: {
        fontSize: 16,
        fontStyle: 'italic',
        fontWeight: 500
      },
      subtitle2: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.13
      },
      body1: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.325
      },
      body2: {
        fontSize: 16,
        fontWeight: 600,
        lineHeight: 1.325
      },
      highlight: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.325,
        color: col[mode].text.dark,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
        fontSize: 16,
      },
      caption: {
        fontSize: 15,
        fontStyle: 'italic',
        fontWeight: 500,
        lineHeight: 1.325
      },
      label: {
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.25
      }
    },
    components: {
      MuiButton: {
        defaultProps: {
          color: 'secondary',
          size: 'small'
        },
        styleOverrides: {
          root: {
            padding: '4px 24px',
            boxShadow: 'none',
            '&.Mui-disabled': {
              color: col[mode].disabled.light
            }/*,
            '&:focus': {
              outline: 'none!important'
            },
            /*'&.MuiButton-sizeSmall': {
              fontSize: '14px',
              letterSpacing: '.25px',
              padding: '0 10px'
            },
            '&.MuiButton-sizeMedium': {
              padding: '0 18px'
            }*/
          },
          contained: {
            '&.MuiButton-containedSecondary': {
              border: '1px solid transparent',
            },
            '&.MuiButton-containedSecondary:not(.Mui-disabled)': {
              color: col[mode].background.default
            },
            '&.MuiButton-containedSecondary:hover': {
              backgroundColor: col[mode].background.default,
              color: col.light.secondary.main,
              border: '1px solid',
              borderColor: col.light.secondary.main,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: mode === 'light' ? 'rgba(11, 94, 130, .07)' : col[mode].disabled.shy,
              border: '1px solid',
              borderColor: col[mode].disabled.shy
            }
          },
          outlined: {
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: col[mode].disabled.light,
              backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, .2)' : 'transparent',
            },
            '&:hover': {
              color: col[mode].background.default,
              backgroundColor: col.light.secondary.main,
            }
          },
          text: {
            '&:hover': {
              backgroundColor: col.light.secondary.light
            }
          }
        }
      },
      MuiButtonGroup: {
        defaultProps: {
          color: 'secondary',
          size: 'small'
        },
        styleOverrides: {
          root: {
            padding: '4px 24px',
            boxShadow: 'none'
          },
          contained: {
            '& .MuiButtonGroup-grouped:not(:hover)': {
              border: '1px solid',
              borderColor: col[mode].background.default,
              '&:not(:last-of-type)': {
                border: '1px solid',
                borderColor: col[mode].background.default
              },
            },
            '&.Mui-disabled': {
              color: col[mode].primary.light,
            }
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            margin: '1.5rem 0 1rem 0',
            borderColor: col[mode].primary.light,
            '&.MuiDivider-root::before': {
              borderColor: col[mode].primary.light
            },
            '&.MuiDivider-root::after': {
              borderColor: col[mode].primary.light
            }
          }
        }
      },
      MuiFab: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            boxShadow: '0px 3px 5px -1px rgba(0,0,0,.05),0px 6px 10px 0px rgba(0,0,0,.05),0px 1px 18px 0px rgba(0,0,0,.05)',
          }
        },
        variants: [
          {
            props: {color: 'primary'},
            style: {
              backgroundColor: col.light.secondary.main,
              color: col[mode].background.default,
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: col.light.secondary.dark
              },
              '&.MuiFab-root.Mui-disabled': {
                backgroundColor: col[mode].disabled.shy
              }
            }
          },
          {
            props: {color: 'primary', variant: 'outlined'}, // was "outlined"
            style: {
              backgroundColor: col[mode].background.default,
              color: col.light.secondary.main,
              border: '1px solid',
              borderColor: col.light.secondary.main,
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: 'rgba(253, 134, 79, 0.08)',
                color: col.light.secondary.dark,
                borderColor: col.light.secondary.dark
              },
              '&.MuiFab-root.Mui-disabled': {
                backgroundColor: col[mode].background.default,
                color: col[mode].disabled.light,
                borderColor: col[mode].disabled.shy
              }
            }
          },
          {
            props: {color: 'secondary'},
            style: {
              backgroundColor: col[mode].primary.main,
              color: col[mode].background.default,
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: col[mode].primary.medium
              },
              '&.MuiFab-root.Mui-disabled': {
                backgroundColor: col[mode].disabled.shy
              }
            }
          },
          {
            props: {color: 'secondary', variant: 'outlined'},
            style: {
              backgroundColor: col[mode].background.default,
              color: col[mode].primary.main,
              border: '1px solid',
              borderColor: col[mode].primary.main,
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: col[mode].background.default,
                color: col[mode].primary.medium,
                borderColor: col[mode].primary.medium
              },
              '&.MuiFab-root.Mui-disabled': {
                backgroundColor: col[mode].background.default,
                color: col[mode].disabled.light,
                borderColor: col[mode].disabled.shy
              }
            }
          },
          {
            props: {variant: 'extended'},
            style: {
              '&.MuiFab-extended': {
                paddingRight: '1rem!important'
              }
            }
          },
          // -- FAB CUSTOM VARIANTS -- //
          {
            props: {variant: 'taskSalesActive'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: `${col.sales.main}!important`,
                color: col.light.primary.main,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.sales.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.sales.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskSales'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: col[mode].background.default,
                color: col[mode].primary.main,
                borderBottom: `4px solid ${col.sales.main}`,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.sales.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.sales.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskPurchaseActive'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: `${col.purchase.main}!important`,
                color: col.light.primary.main,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.purchase.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.purchase.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskPurchase'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: col[mode].background.default,
                color: col[mode].primary.main,
                borderBottom: `4px solid ${col.purchase.main}`,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.purchase.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.purchase.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskAccountingActive'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: `${col.accounting.main}!important`,
                color: col.light.primary.main,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.accounting.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.accounting.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskAccounting'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: col[mode].background.default,
                color: col[mode].primary.main,
                borderBottom: `4px solid ${col.accounting.main}`,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.accounting.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.accounting.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskQmActive'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: `${col.qm.main}!important`,
                color: col.light.primary.main,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.qm.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.qm.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskQm'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: col[mode].background.default,
                color: col[mode].primary.main,
                borderBottom: `4px solid ${col.qm.main}`,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.qm.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.qm.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskPackagingActive'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: `${col.packaging.main}!important`,
                color: col.light.primary.main,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.packaging.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.packaging.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskPackaging'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: col[mode].background.default,
                color: col[mode].primary.main,
                borderBottom: `4px solid ${col.packaging.main}`,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.packaging.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.packaging.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskLogisticsActive'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: `${col.logistics.main}!important`,
                color: col.light.primary.main,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.logistics.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.logistics.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          },
          {
            props: {variant: 'taskLogistics'},
            style: {
              '&.MuiFab-root': {
                width: 'auto',
                paddingLeft: '.8rem',
                paddingRight: '.8rem',
                marginRight: '20px',
                borderRadius: '0',
                backgroundColor: col[mode].background.default,
                color: col[mode].primary.main,
                borderBottom: `4px solid ${col.logistics.main}`,
              },
              '&.MuiFab-root:not(.Mui-disabled):hover': {
                backgroundColor: `${col.logistics.main}!important`,
                opacity: '.75'
              },
              '&.MuiFab-root:after': {
                content: '""',
                position: 'absolute',
                height: '0',
                width: '0',
                left: '100%',
                top: '0',
                border: '20px solid transparent',
                borderLeft: '20px solid',
                borderLeftColor: `${col.logistics.main}`
              },
              '&.MuiFab-root.Mui-disabled': {
                opacity: '.60'
              }
            }
          }
        ],
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiInput-root': {
              '&:before': {
                borderBottom: `1px solid ${col.light.quartary.medium}`
              },
              '&:hover:before': {
                borderBottom: `1px solid ${col.light.quartary.main}`
              }
            },
            '& .MuiInputLabel-root:not(.Mui-error)': {
              color: col[mode].tertiary.main
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: col[mode].tertiary.light, // was main in 1.9.0
              },
              '&:hover fieldset': {
                borderColor: col[mode].tertiary.medium,
              },
              '&.Mui-focused fieldset': {
                borderColor: col[mode].tertiary.medium,
              }
            },
            '& .MuiInputBase-root.MuiFilledInput-root': {
              backgroundColor: col[mode].quartary.light,
              '& .MuiInputBase-input.MuiFilledInput': {
                color: col[mode].primary.main
              }
            }
          }
        }
      },
      MuiCheckbox: {
        defaultProps: {
          size: 'small'
        },
        styleOverrides: {
          root: {
            padding: '4px',
            '&.Mui-disabled': {
              color: col[mode].primary.light
            }
          }
        }
      },
      MuiRadio: {
        defaultProps: {
          size: 'small'
        },
        styleOverrides: {
          root: {
            padding: '4px',
          }
        }
      },
      MuiToggleButton: {
        defaultProps: {
          size: 'small'
        },
        styleOverrides: {
          root: {
            '&.MuiToggleButton-root:not(.Mui-selected):not(.Mui-disabled)': {
              color: col[mode].primary.medium
            },
            '&.Mui-disabled': {
              color: col[mode].primary.light
            }
          }
        }
      },
      MuiAvatar: {
        variants: [
          {
            props: {variant: 'primary'},
            style: {
              backgroundColor: col.light.secondary.main,
              color: col[mode].background.default
            },
          },
          {
            props: {variant: 'sales'},
            style: {
              backgroundColor: `${col.sales.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'purchase'},
            style: {
              backgroundColor: `${col.purchase.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'accounting'},
            style: {
              backgroundColor: `${col.accounting.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'qm'},
            style: {
              backgroundColor: `${col.qm.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'packaging'},
            style: {
              backgroundColor: `${col.packaging.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'logistics'},
            style: {
              backgroundColor: `${col.logistics.main}`,
              color: 'white'
            },
          },
        ]
      },
      MuiChip: {
        variants: [
          {
            props: {variant: 'normal'},
            style: {
              backgroundColor: `${col[mode].quartary.light}`,
              color: `${col[mode].text.primary}`
            },
          },
          {
            props: {variant: 'primary'},
            style: {
              backgroundColor: `${col[mode].primary.main}`,
              color: `white`
            },
          },
          {
            props: {variant: 'secondary'},
            style: {
              backgroundColor: `${col.light.secondary.main}`,
              color: `white`
            },
          },
          {
            props: {variant: 'sales'},
            style: {
              backgroundColor: `${col.sales.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'purchase'},
            style: {
              backgroundColor: `${col.purchase.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'accounting'},
            style: {
              backgroundColor: `${col.accounting.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'qm'},
            style: {
              backgroundColor: `${col.qm.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'packaging'},
            style: {
              backgroundColor: `${col.packaging.main}`,
              color: `${col[mode].text.contrast}`
            },
          },
          {
            props: {variant: 'logistics'},
            style: {
              backgroundColor: `${col.logistics.main}`,
              color: 'white'
            },
          },
        ],
        styleOverrides: {
          root: {
            '&.MuiChip-root': {
              borderRadius: '3px'
            },
            '&.MuiChip-outlined:not(.MuiChip-colorError):not(.MuiChip-colorWarning):not(.MuiChip-colorSuccess)': {
              borderColor: col[mode].primary.main
            }
          }
        }
      },
      MuiStack: {
        styleOverrides: {
          root: {
            '& .MuiRating-iconFilled': {
              color: col.light.secondary.main,
            },
            '& .MuiRating-iconHover': {
              color: col.light.secondary.dark,
            }
          }
        }
      },
      MuiTable: {
        variants: [
          {
            props: {variant: 'striped'},
            style: {
              '& .MuiTableHead-root': {
                backgroundColor: col[mode].tertiary.shy,
                borderColor: col[mode].tertiary.light,
              },
              '& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root': {
                backgroundColor: col[mode].quartary.shy
              }
            }
          }
        ],
        styleOverrides: {
          root: {
            '& .MuiTableHead-root': {
              backgroundColor: col[mode].tertiary.shy,
              borderColor: col.light.tertiary.light,
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              borderBottomWidth: '2px'
            },
            '& .MuiTableCell-root': {
              borderColor: col.light.tertiary.light,
            }
          }
        }
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            '&.MuiDataGrid-root': {
              borderColor: col.light.tertiary.light,
            },
            '& .MuiDataGrid-cell': {
              borderBottomColor: col.light.tertiary.light,
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottomWidth: '2px',
              borderBottomColor: col.light.tertiary.light,
              backgroundColor: col[mode].tertiary.shy,
            },
            '& .MuiDataGrid-columnSeparator': {
              color: col.light.tertiary.light,
            },
            '& .MuiButtonBase-root:not(.MuiCheckbox-root):not(.Mui-disabled) .MuiSvgIcon-root': {
              color: col.light.secondary.main,
              fill: col.light.secondary.main,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: col[mode].quartary.light,
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: col[mode].tertiary.light,
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: col[mode].tertiary.light,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTopColor: col.light.tertiary.light,
            },
            '& .MuiTablePagination-actions .MuiButtonBase-root:not(.Mui-disabled)': {
              color: col.light.secondary.main,
            }
          },
        }
      },
      MuiList: {
        styleOverrides: {
          root: {
            '&.MuiList-root': {
              backgroundColor: col[mode].background.default
            }
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '&.MuiListItem-root:not(.Mui-disabled):hover': {
              backgroundColor: col[mode].quartary.light
            }
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            '&.MuiListItemIcon-root': {
              color: col[mode].primary.main,
            }
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.MuiMenuItem-root:hover': {
              backgroundColor: col[mode].quartary.light,
            }
          }
        }
      },
      MuiAccordionSummary: {
        variants: [
          {
            props: {variant: 'sales'},
            style: {
              borderLeftWidth: '8px',
              borderLeftStyle: 'solid',
              borderLeftColor: `${col.sales.main}`
            },
          },
          {
            props: {variant: 'purchase'},
            style: {
              borderLeftWidth: '8px',
              borderLeftStyle: 'solid',
              borderLeftColor: `${col.purchase.main}`
            },
          },
          {
            props: {variant: 'accounting'},
            style: {
              borderLeftWidth: '8px',
              borderLeftStyle: 'solid',
              borderLeftColor: `${col.accounting.main}`
            },
          },
          {
            props: {variant: 'qm'},
            style: {
              borderLeftWidth: '8px',
              borderLeftStyle: 'solid',
              borderLeftColor: `${col.qm.main}`
            },
          },
          {
            props: {variant: 'packaging'},
            style: {
              borderLeftWidth: '8px',
              borderLeftStyle: 'solid',
              borderLeftColor: `${col.packaging.main}`
            },
          },
          {
            props: {variant: 'logistics'},
            style: {
              borderLeftWidth: '8px',
              borderLeftStyle: 'solid',
              borderLeftColor: `${col.logistics.main}`
            },
          },
          {
            props: {variant: 'level1'},
            style: {
              backgroundColor: `${col[mode].background.level.l1}!important`
            },
          },
          {
            props: {variant: 'level2'},
            style: {
              backgroundColor: `${col[mode].background.level.l2}!important`
            },
          },
          {
            props: {variant: 'level3'},
            style: {
              backgroundColor: `${col[mode].background.level.l3}!important`
            },
          },
          {
            props: {variant: 'level4'},
            style: {
              backgroundColor: `${col[mode].background.level.l4}!important`
            }
          }
        ]
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            '&.MuiAccordion-root': {
              border: '1px solid',
              borderColor: col.light.quartary.main,
              borderBottomWidth: '2px',
              boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 15%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%);',
              marginBottom: '.6rem',
              borderRadius: '3px 3px 0 0',
              '&.Mui-expanded': {
                marginTop: '1rem',
                marginBottom: '1.2rem'
              }
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: col.light.secondary.dark
            },
            '& .MuiAccordionSummary-root': { // Accordion Header
              paddingLeft: '.86rem',
              backgroundColor: 'rgba(22, 146, 178, .15)', // Color header
              fontWeight: 600,
              '&.Mui-disabled': {
                backgroundColor: 'rgba(22, 146, 178, .07)',
              }
            },
            '& .MuiAccordionDetails-root': {
              padding: '1.2rem .85rem 1rem .85rem'
            }
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            color: col.light.primary.main, // always use mode light here due to transparency issues
            backgroundColor: col.light.tertiary.medium,
            '& .MuiTooltip-arrow:before': {
              backgroundColor: col.light.tertiary.medium
            }
          }
        }
      },
      MuiAlert: {
        variants: [
          {
            props: {severity: 'error'},
            style: {
              '&.MuiAlert-root': {
                backgroundColor: col.error.passive
              },
              '& .MuiAlert-icon': {
                color: col.error.main
              }
            }
          },
          {
            props: {severity: 'warning'},
            style: {
              '&.MuiAlert-root': {
                backgroundColor: col.warning.passive
              },
              '& .MuiAlert-icon': {
                color: col.warning.main
              }
            }
          },
          {
            props: {severity: 'success'},
            style: {
              '&.MuiAlert-root': {
                backgroundColor: col.success.passive
              },
              '& .MuiAlert-icon': {
                color: col.success.main
              }
            }
          },
          {
            props: {severity: 'info'},
            style: {
              '&.MuiAlert-root': {
                backgroundColor: col.info.passive
              },
              '& .MuiAlert-icon': {
                color: col.info.main
              }
            }
          }
        ]
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            '&.MuiAlertTitle-root': {
              fontWeight: 600
            }
          }
        }
      },
      MuiLink: {
        styleOverrides: {
          root: {
            '&.MuiLink-root': {
              fontFamily: `"Source Sans Pro", Helvetica, Arial, sans-serif`,
              color: col.light.secondary.dark,
              textDecorationColor: col.light.secondary.dark
            }
          }
        }
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root:hover': {
              backgroundColor: col[mode].tertiary.light
            },
            '& .MuiPaginationItem-root.Mui-selected': {
              backgroundColor: col[mode].quartary.light,
              '&:hover': {
                backgroundColor: col[mode].tertiary.light
              }
            },
            '& .MuiPaginationItem-root.MuiPaginationItem-textPrimary:hover': {
              backgroundColor: col.light.secondary.shy
            },
            '& .MuiPaginationItem-root.MuiPaginationItem-textPrimary.Mui-selected': {
              color: col[mode].background.default,
              backgroundColor: col.light.secondary.main,
              '&:hover': {
                backgroundColor: col.light.secondary.dark
              }
            },
            '& .MuiPaginationItem-root.MuiPaginationItem-textSecondary:hover': {
              backgroundColor: col[mode].tertiary.light
            },
            '& .MuiPaginationItem-root.MuiPaginationItem-textSecondary.Mui-selected': {
              color: col[mode].background.default,
              backgroundColor: col[mode].primary.main,
              '&:hover': {
                backgroundColor: col[mode].tertiary.light
              }
            },
            '& .MuiPaginationItem-root.Mui-selected.Mui-disabled': {
              backgroundColor: col[mode].disabled.shy
            }
          }
        }
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            '& .MuiStepConnector-line': {
              border: `1px dashed ${col[mode].primary.main}`
            }
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            '&.MuiTabs-root': {
              borderColor: col[mode].primary.light
            }
          }
        },
        variants: [
          {
            props: {type: 'indexCard'},
            style: {
              '&.MuiTabs-root .MuiTab-root': {
                borderTopWidth: '1px',
                borderTopStyle: 'solid',
                borderLeftWidth: '1px',
                borderLeftStyle: 'solid',
                borderRightWidth: '1px',
                borderRightStyle: 'solid',
                borderTopLeftRadius: '5px !important',
                borderTopRightRadius: '5px !important',
                marginRight: '1px',
              }
            }
          }
        ]
      }
    }
  }

  return theme;
}

export {FactureeTheme};
